/**
 * Workspace Service
 *
 * API service for workspace multi-tenant operations
 */

import axiosInstance from './axiosConfig';
import {
  Workspace,
  WorkspaceWithStats,
  WorkspaceMember,
  WorkspaceGroup,
  WorkspaceGroupMember,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  AddWorkspaceMemberRequest,
  UpdateWorkspaceMemberRequest,
  CreateWorkspaceGroupRequest,
  UpdateWorkspaceGroupRequest,
  AddGroupMemberRequest,
  PermissionCheckResponse,
} from '../types/workspace';

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  metadata?: Record<string, unknown>;
}

// Paginated response wrapper
interface PaginatedResponse<T> {
  workspaces?: T[];
  total: number;
  skip: number;
  limit: number;
}

// =====================================================================
// WORKSPACE OPERATIONS
// =====================================================================

/**
 * Get all workspaces accessible to the user (including administrative access)
 *
 * For TD/superadmins: Returns ALL workspaces (for admin management)
 * For regular users: Returns workspaces where they are owner/member
 *
 * If requiredPermission is specified, only returns workspaces where the user
 * has that specific permission (e.g., 'can_view_analytics')
 */
export const getUserWorkspaces = async (
  skip = 0,
  limit = 100,
  requiredPermission?: string
): Promise<ApiResponse<Workspace[]>> => {
  const params: Record<string, unknown> = { skip, limit };
  if (requiredPermission) {
    params.required_permission = requiredPermission;
  }

  const response = await axiosInstance.get<ApiResponse<PaginatedResponse<Workspace>>>('/workspaces', {
    params,
  });

  return {
    ...response.data,
    data: response.data.data.workspaces || [],
  };
};

/**
 * Get ONLY workspaces where user is a direct member or owner
 *
 * This endpoint is for WorkspaceSwitcher - shows only workspaces the user
 * can work in, NOT administrative access.
 *
 * TD users will ONLY see workspaces where they are actual members.
 */
export const getUserWorkspaceMemberships = async (): Promise<ApiResponse<Workspace[]>> => {
  const response = await axiosInstance.get<ApiResponse<PaginatedResponse<Workspace>>>('/workspaces/my-memberships');

  // Transform paginated response to simple array for compatibility
  return {
    ...response.data,
    data: response.data.data.workspaces || [],
  };
};

/**
 * Get all workspaces in the system (Admin view)
 * For TD users, this returns ALL workspaces. For regular users, returns only accessible workspaces.
 * This is an alias for getUserWorkspaces() with semantic naming for admin context.
 */
export const getAllWorkspaces = async (
  skip = 0,
  limit = 100 // Backend limit is 100
): Promise<ApiResponse<PaginatedResponse<Workspace>>> => {
  const response = await axiosInstance.get<ApiResponse<PaginatedResponse<Workspace>>>('/workspaces', {
    params: { skip, limit },
  });

  return response.data;
};

/**
 * Get workspace by ID
 */
export const getWorkspace = async (workspaceId: number): Promise<ApiResponse<WorkspaceWithStats>> => {
  const response = await axiosInstance.get<ApiResponse<WorkspaceWithStats>>(`/workspaces/${workspaceId}`);
  return response.data;
};

/**
 * Create a new workspace
 */
export const createWorkspace = async (data: CreateWorkspaceRequest): Promise<ApiResponse<Workspace>> => {
  const response = await axiosInstance.post<ApiResponse<Workspace>>('/workspaces', data);
  return response.data;
};

/**
 * Update workspace
 */
export const updateWorkspace = async (
  workspaceId: number,
  data: UpdateWorkspaceRequest
): Promise<ApiResponse<Workspace>> => {
  const response = await axiosInstance.put<ApiResponse<Workspace>>(`/workspaces/${workspaceId}`, data);
  return response.data;
};

/**
 * Delete workspace (owner only)
 */
export const deleteWorkspace = async (workspaceId: number): Promise<ApiResponse<{ workspace_id: number }>> => {
  const response = await axiosInstance.delete<ApiResponse<{ workspace_id: number }>>(`/workspaces/${workspaceId}`);
  return response.data;
};

/**
 * Check user permission in workspace
 */
export const checkPermission = async (
  workspaceId: number,
  permission: string
): Promise<ApiResponse<PermissionCheckResponse>> => {
  const response = await axiosInstance.get<ApiResponse<PermissionCheckResponse>>(
    `/workspaces/${workspaceId}/permissions/${permission}`
  );
  return response.data;
};

/**
 * Switch active workspace (with backend validation)
 *
 * SECURITY: Backend validates that user has access to the workspace.
 * This prevents localStorage manipulation attacks.
 */
export const switchWorkspace = async (workspaceId: number): Promise<ApiResponse<Workspace>> => {
  const response = await axiosInstance.post<ApiResponse<Workspace>>(`/workspaces/switch/${workspaceId}`);
  return response.data;
};

// =====================================================================
// WORKSPACE MEMBER OPERATIONS
// =====================================================================

/**
 * Get all members of a workspace
 */
export const getWorkspaceMembers = async (
  workspaceId: number,
  skip = 0,
  limit = 1000
): Promise<ApiResponse<WorkspaceMember[]>> => {
  const response = await axiosInstance.get<ApiResponse<WorkspaceMember[]>>(
    `/workspaces/${workspaceId}/members`,
    {
      params: { skip, limit },
    }
  );
  return response.data;
};

/**
 * Add member to workspace
 */
export const addWorkspaceMember = async (
  workspaceId: number,
  data: AddWorkspaceMemberRequest
): Promise<ApiResponse<WorkspaceMember>> => {
  const response = await axiosInstance.post<ApiResponse<WorkspaceMember>>(
    `/workspaces/${workspaceId}/members`,
    data
  );
  return response.data;
};

/**
 * Update workspace member permissions
 */
export const updateWorkspaceMember = async (
  workspaceId: number,
  userId: number,
  data: UpdateWorkspaceMemberRequest
): Promise<ApiResponse<WorkspaceMember>> => {
  const response = await axiosInstance.put<ApiResponse<WorkspaceMember>>(
    `/workspaces/${workspaceId}/members/${userId}`,
    data
  );
  return response.data;
};

/**
 * Remove member from workspace
 */
export const removeWorkspaceMember = async (
  workspaceId: number,
  userId: number
): Promise<ApiResponse<{ workspace_id: number; user_id: number }>> => {
  const response = await axiosInstance.delete<ApiResponse<{ workspace_id: number; user_id: number }>>(
    `/workspaces/${workspaceId}/members/${userId}`
  );
  return response.data;
};

// =====================================================================
// WORKSPACE GROUP OPERATIONS
// =====================================================================

/**
 * Get all groups in a workspace
 */
export const getWorkspaceGroups = async (
  workspaceId: number,
  skip = 0,
  limit = 100,
  includeDeleted = false
): Promise<ApiResponse<WorkspaceGroup[]>> => {
  const response = await axiosInstance.get<ApiResponse<WorkspaceGroup[]>>(
    `/workspaces/${workspaceId}/groups`,
    {
      params: { skip, limit, include_deleted: includeDeleted },
    }
  );
  return response.data;
};

/**
 * Create a new group in workspace
 */
export const createWorkspaceGroup = async (
  workspaceId: number,
  data: CreateWorkspaceGroupRequest
): Promise<ApiResponse<WorkspaceGroup>> => {
  const response = await axiosInstance.post<ApiResponse<WorkspaceGroup>>(
    `/workspaces/${workspaceId}/groups`,
    data
  );
  return response.data;
};

/**
 * Update workspace group
 */
export const updateWorkspaceGroup = async (
  workspaceId: number,
  groupId: number,
  data: UpdateWorkspaceGroupRequest
): Promise<ApiResponse<WorkspaceGroup>> => {
  const response = await axiosInstance.put<ApiResponse<WorkspaceGroup>>(
    `/workspaces/${workspaceId}/groups/${groupId}`,
    data
  );
  return response.data;
};

/**
 * Delete workspace group (soft delete)
 */
export const deleteWorkspaceGroup = async (
  workspaceId: number,
  groupId: number
): Promise<ApiResponse<{ workspace_id: number; group_id: number }>> => {
  const response = await axiosInstance.delete<ApiResponse<{ workspace_id: number; group_id: number }>>(
    `/workspaces/${workspaceId}/groups/${groupId}`
  );
  return response.data;
};

// =====================================================================
// GROUP MEMBER OPERATIONS
// =====================================================================

/**
 * Get all members of a workspace group
 */
export const getGroupMembers = async (
  workspaceId: number,
  groupId: number,
  skip = 0,
  limit = 100
): Promise<ApiResponse<WorkspaceGroupMember[]>> => {
  const response = await axiosInstance.get<ApiResponse<WorkspaceGroupMember[]>>(
    `/workspaces/${workspaceId}/groups/${groupId}/members`,
    {
      params: { skip, limit },
    }
  );
  return response.data;
};

/**
 * Add member to workspace group
 */
export const addGroupMember = async (
  workspaceId: number,
  groupId: number,
  data: AddGroupMemberRequest
): Promise<ApiResponse<WorkspaceGroupMember>> => {
  const response = await axiosInstance.post<ApiResponse<WorkspaceGroupMember>>(
    `/workspaces/${workspaceId}/groups/${groupId}/members`,
    data
  );
  return response.data;
};

/**
 * Remove member from workspace group
 */
export const removeGroupMember = async (
  workspaceId: number,
  groupId: number,
  userId: number
): Promise<ApiResponse<{ workspace_id: number; group_id: number; removed_user_id: number }>> => {
  const response = await axiosInstance.delete<
    ApiResponse<{ workspace_id: number; group_id: number; removed_user_id: number }>
  >(`/workspaces/${workspaceId}/groups/${groupId}/members/${userId}`);
  return response.data;
};

export default {
  getUserWorkspaces,
  getUserWorkspaceMemberships,
  getAllWorkspaces,
  getWorkspace,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  checkPermission,
  switchWorkspace,
  getWorkspaceMembers,
  addWorkspaceMember,
  updateWorkspaceMember,
  removeWorkspaceMember,
  getWorkspaceGroups,
  createWorkspaceGroup,
  updateWorkspaceGroup,
  deleteWorkspaceGroup,
  getGroupMembers,
  addGroupMember,
  removeGroupMember,
};
